/**
 * ============================================================================
 * USER / TENANT PAGE: WhatsApp Live Chat & Two-Way Customer Conversations
 * ============================================================================
 * Purpose:
 *   High-fidelity WhatsApp Web Live Chat Interface:
 *   - Edge-to-edge full-bleed responsive layout (mobile back button & split views)
 *   - Inbound chats from Webhook ends (Meta Cloud API, Karix, Pinnacle)
 *   - Left-side & right-side user avatars for each message bubble
 *   - Authentic WhatsApp status indicators (sent, delivered, read, failed) & timestamps
 *   - Multi-media attachment menu (Photos & Videos, Documents, Audio) with pre-send
 *     staged file tray, thumbnail previews, individual remove buttons, and captions
 *   - Integrated Emoji Picker (emoji-picker-react) with light/dark theme
 *   - Exact text formatting preservation (newlines, spaces, indents, clickable URLs)
 *   - Shift + Enter for newlines & Enter to send
 * ============================================================================
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import EmojiPicker, { Theme } from "emoji-picker-react";
import {
  FaWhatsapp,
  FaImage,
  FaVideo,
  FaFileAlt,
  FaHeadphones,
} from "react-icons/fa";
import {
  IoCheckmark,
  IoCheckmarkDone,
  IoAlertCircle,
  IoTimeOutline,
} from "react-icons/io5";
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  PaperClipIcon,
  FaceSmileIcon,
  BookmarkIcon,
  XMarkIcon,
  DocumentIcon,
} from "@heroicons/react/24/outline";
import { PaperAirplaneIcon } from "@heroicons/react/24/solid";

import { chatService } from "../../services";
import { SERVER_BASE_URL } from "../../../core/config/constants";
import { getCurrentUser } from "../../../core/utils/auth";

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

// Dynamic avatar background generator based on name/phone hash
function getAvatarColor(nameOrPhone) {
  const colors = [
    "bg-emerald-600",
    "bg-blue-600",
    "bg-indigo-600",
    "bg-purple-600",
    "bg-rose-600",
    "bg-amber-600",
    "bg-teal-600",
    "bg-cyan-600",
  ];
  let hash = 0;
  const str = String(nameOrPhone || "user");
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

// Conversation list timestamp formatter
function formatConversationTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
  }
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) {
    return d.toLocaleDateString([], { weekday: "short" });
  }
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

// WhatsApp message bubble time formatter (e.g. 03:16 PM)
function formatMessageTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
}

// WhatsApp centered date separator pill label (e.g. TODAY, YESTERDAY, Mon, Oct 16)
function formatDateSeparator(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msgDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((today - msgDate) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: d.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
}

// Resolves relative upload paths to the backend server origin
function resolveMediaUrl(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("blob:") || url.startsWith("data:")) {
    return url;
  }
  return `${SERVER_BASE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
}

// Auto-hyperlinks URLs while preserving exact text formatting, newlines, and indentation
function renderFormattedText(text) {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, index) => {
    if (part.match(urlRegex)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#027eb5] dark:text-[#53bdeb] underline hover:opacity-80 break-all"
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </a>
      );
    }
    return part;
  });
}

// ============================================================================
// WHATSAPP STATUS CHECKMARK COMPONENT (Sent, Delivered, Read, Failed, Pending)
// ============================================================================
function MessageStatus({ status, time }) {
  const normalized = String(status || "sent").toLowerCase();

  return (
    <div className="flex items-center justify-end gap-1 text-[11px] leading-none select-none shrink-0">
      <span className="text-[#667781] dark:text-[#8696a0] font-sans">{time}</span>

      {normalized === "read" ? (
        <span title="Read">
          <IoCheckmarkDone className="w-4 h-4 text-[#53bdeb] inline" />
        </span>
      ) : normalized === "delivered" ? (
        <span title="Delivered">
          <IoCheckmarkDone className="w-4 h-4 text-[#8696a0] inline" />
        </span>
      ) : normalized === "failed" ? (
        <span title="Failed to deliver" className="inline-flex items-center gap-0.5 text-rose-500 font-semibold">
          <IoAlertCircle className="w-3.5 h-3.5 text-rose-500 inline" />
          <span className="text-[10px]">Failed</span>
        </span>
      ) : normalized === "pending" || normalized === "sending" ? (
        <span title="Sending">
          <IoTimeOutline className="w-3.5 h-3.5 text-[#8696a0] animate-pulse inline" />
        </span>
      ) : (
        /* default: sent (single checkmark) */
        <span title="Sent">
          <IoCheckmark className="w-4 h-4 text-[#8696a0] inline" />
        </span>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT: CHAT
// ============================================================================
export default function Chat() {
  const [conversations, setConversations] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [messageInput, setMessageInput] = useState("");
  const [sending, setSending] = useState(false);

  // Emoji Picker & Attachment Popup state
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);

  // Staged files for multi-select media attachments
  // Array of: { id, file, name, size, type: 'image'|'video'|'audio'|'document', previewUrl }
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const emojiPickerContainerRef = useRef(null);
  const attachMenuRef = useRef(null);

  // Category file input refs for WhatsApp attachment menu
  const imageVideoInputRef = useRef(null);
  const docInputRef = useRef(null);
  const audioInputRef = useRef(null);

  // Current logged in user details for outgoing message avatars
  const currentUser = getCurrentUser();
  const currentUserName = currentUser?.str_name || currentUser?.str_username || "Me";
  const currentUserInitial = currentUserName.slice(0, 2).toUpperCase();

  // Detect dark theme
  const isDarkMode = typeof document !== "undefined" && document.documentElement.classList.contains("dark");

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  /**
   * Load Conversations List from Webhook messages
   */
  const loadConversations = useCallback(async () => {
    try {
      const res = await chatService.getConversations({
        q: searchQuery,
        filter: activeFilter,
      });
      if (res?.success) {
        setConversations(res.conversations || []);
      }
    } catch (err) {
      console.error("Failed to load conversations:", err);
    } finally {
      setLoadingConversations(false);
    }
  }, [searchQuery, activeFilter]);

  useEffect(() => {
    loadConversations();
    const interval = setInterval(loadConversations, 5000);
    return () => clearInterval(interval);
  }, [loadConversations]);

  /**
   * Load Message History for Active Conversation
   */
  const loadMessages = useCallback(async (phone, silent = false) => {
    if (!phone) return;
    if (!silent) setLoadingMessages(true);
    try {
      const res = await chatService.getMessages(phone);
      if (res?.success) {
        setMessages(res.messages || []);
      }
    } catch (err) {
      console.error("Failed to load messages:", err);
    } finally {
      if (!silent) setLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    if (selectedContact) {
      const contactPhone = selectedContact.str_sender_id || selectedContact.str_receiver_id;
      loadMessages(contactPhone);
      const msgInterval = setInterval(() => loadMessages(contactPhone, true), 3500);
      return () => clearInterval(msgInterval);
    }
  }, [selectedContact, loadMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Click outside listener for Emoji Picker and Attachment Menu
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        emojiPickerContainerRef.current &&
        !emojiPickerContainerRef.current.contains(event.target)
      ) {
        setShowEmojiPicker(false);
      }
      if (
        attachMenuRef.current &&
        !attachMenuRef.current.contains(event.target)
      ) {
        setShowAttachMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Auto-resize textarea height
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [messageInput]);

  /**
   * Handle Emoji Selection from emoji-picker-react
   */
  const handleEmojiClick = (emojiData) => {
    if (!emojiData?.emoji) return;
    setMessageInput((prev) => prev + emojiData.emoji);
    textareaRef.current?.focus();
  };

  /**
   * Handle Multiple File Selection from file inputs
   */
  const handleFilesSelect = (e) => {
    const fileList = Array.from(e.target.files || []);
    if (fileList.length === 0) return;

    const newEntries = fileList.map((file) => {
      const isImg = file.type.startsWith("image/");
      const isVid = file.type.startsWith("video/");
      const isAud = file.type.startsWith("audio/");
      const fileType = isImg ? "image" : isVid ? "video" : isAud ? "audio" : "document";

      return {
        id: `file_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`,
        file,
        name: file.name,
        size: file.size,
        type: fileType,
        previewUrl: isImg ? URL.createObjectURL(file) : null,
      };
    });

    setSelectedFiles((prev) => [...prev, ...newEntries]);
    setShowAttachMenu(false);
    // Reset input value so selecting the same file again triggers onChange
    e.target.value = "";
  };

  /**
   * Remove a single file from the staged preview tray before sending
   */
  const removeFile = (id) => {
    setSelectedFiles((prev) => {
      const target = prev.find((f) => f.id === id);
      if (target?.previewUrl) {
        URL.revokeObjectURL(target.previewUrl);
      }
      return prev.filter((f) => f.id !== id);
    });
  };

  /**
   * Clear all staged files
   */
  const clearAllFiles = () => {
    selectedFiles.forEach((f) => f.previewUrl && URL.revokeObjectURL(f.previewUrl));
    setSelectedFiles([]);
  };

  /**
   * Keydown: Enter sends message, Shift+Enter creates a newline
   */
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent?.isComposing) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  /**
   * Send Message & Upload Selected Media Files
   */
  const handleSendMessage = async (e) => {
    if (e) e.preventDefault();
    if ((!messageInput.trim() && selectedFiles.length === 0) || !selectedContact || sending) return;

    const contactPhone = selectedContact.str_sender_id || selectedContact.str_receiver_id;
    const textToSend = messageInput.trim();
    const filesToSend = [...selectedFiles];

    setMessageInput("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";
    setShowEmojiPicker(false);
    setShowAttachMenu(false);
    setSelectedFiles([]);
    setSending(true);

    try {
      // 1. If media files are selected, upload and send each file
      if (filesToSend.length > 0) {
        setUploadingMedia(true);

        for (let i = 0; i < filesToSend.length; i++) {
          const item = filesToSend[i];
          let uploadedUrl = null;

          try {
            const uploadRes = await chatService.uploadMedia(item.file);
            uploadedUrl = uploadRes?.data?.url || uploadRes?.url;
          } catch (uploadErr) {
            console.error("Upload failed for file:", item.name, uploadErr);
          }

          // If text was typed, attach caption to the first or last media item
          const caption = i === filesToSend.length - 1 ? textToSend : "";

          // Optimistic local bubble
          const tempId = `temp_${Date.now()}_${i}`;
          const optimisticBubble = {
            _id: tempId,
            str_message_id: tempId,
            str_message: caption,
            str_message_type: item.type,
            str_media_url: uploadedUrl || item.previewUrl,
            str_status: uploadedUrl ? "sent" : "failed",
            bool_is_from_webhook: false,
            dttm_created_on: new Date().toISOString(),
          };
          setMessages((prev) => [...prev, optimisticBubble]);

          // Dispatch to backend API
          if (uploadedUrl) {
            await chatService.sendMessage({
              to: contactPhone,
              message: caption,
              messageType: item.type,
              mediaUrl: uploadedUrl,
              businessNumber: selectedContact.str_business_number || "",
            });
          }

          // Clean up blob url
          if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
        }
        setUploadingMedia(false);
      } else if (textToSend) {
        // 2. Plain text message dispatch
        const tempId = `temp_${Date.now()}`;
        const optimisticMessage = {
          _id: tempId,
          str_message_id: tempId,
          str_message: textToSend,
          str_message_type: "text",
          str_media_url: null,
          str_status: "sent",
          bool_is_from_webhook: false,
          dttm_created_on: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, optimisticMessage]);

        await chatService.sendMessage({
          to: contactPhone,
          message: textToSend,
          messageType: "text",
          mediaUrl: null,
          businessNumber: selectedContact.str_business_number || "",
        });
      }

      loadMessages(contactPhone, true);
      loadConversations();
    } catch (err) {
      console.error("Failed to send message:", err);
    } finally {
      setSending(false);
      setUploadingMedia(false);
    }
  };

  /**
   * Toggle Pin Conversation
   */
  const handleTogglePin = async (e, conv) => {
    e.stopPropagation();
    try {
      await chatService.togglePin(conv._id, !conv.bool_is_pinned);
      loadConversations();
      if (selectedContact?._id === conv._id) {
        setSelectedContact((prev) => ({ ...prev, bool_is_pinned: !conv.bool_is_pinned }));
      }
    } catch (err) {
      console.error("Failed to toggle pin:", err);
    }
  };

  return (
    <div className="h-full w-full flex overflow-hidden bg-white dark:bg-[#111b21] text-[#111b21] dark:text-[#e9edef] select-text">
      {/* ==================================================================== */}
      {/* LEFT COLUMN: CONVERSATION LIST (Responsive: Hidden on mobile when chat open) */}
      {/* ==================================================================== */}
      <div
        className={`${
          selectedContact ? "hidden md:flex" : "flex"
        } w-full md:w-80 lg:w-[340px] shrink-0 flex-col border-r border-[#e9edef] dark:border-[#222d34] bg-white dark:bg-[#111b21] h-full z-10`}
      >
        {/* Top Header */}
        <div className="h-16 px-4 bg-[#f0f2f5] dark:bg-[#202c33] flex items-center justify-between border-b border-[#e9edef] dark:border-[#222d34] shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#00a884] text-white flex items-center justify-center font-bold text-sm shadow-xs select-none">
              <FaWhatsapp className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-[#111b21] dark:text-[#e9edef] leading-tight">
                Chats
              </h2>
              <p className="text-[11px] text-[#667781] dark:text-[#8696a0]">
                {conversations.length} active customer thread{conversations.length === 1 ? "" : "s"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={loadConversations}
              className="p-2 text-[#54656f] dark:text-[#aebac1] hover:bg-black/5 dark:hover:bg-white/5 rounded-full transition-colors"
              title="Refresh Conversations"
            >
              <ArrowPathIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="p-2.5 bg-white dark:bg-[#111b21] border-b border-[#f0f2f5] dark:border-[#202c33] shrink-0">
          <div className="relative flex items-center bg-[#f0f2f5] dark:bg-[#202c33] rounded-lg px-3 py-1.5">
            <MagnifyingGlassIcon className="w-4 h-4 text-[#54656f] dark:text-[#8696a0] mr-2 shrink-0" />
            <input
              type="text"
              placeholder="Search conversations or phone"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-transparent text-xs text-[#111b21] dark:text-[#e9edef] placeholder-[#667781] dark:placeholder-[#8696a0] focus:outline-none"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery("")} className="text-xs text-[#54656f] dark:text-[#8696a0] ml-1">
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex items-center gap-1 px-3 py-2 bg-white dark:bg-[#111b21] border-b border-[#f0f2f5] dark:border-[#202c33] text-xs shrink-0">
          {["all", "unread", "pinned"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveFilter(tab)}
              className={`px-3 py-1 rounded-full font-medium capitalize text-xs transition-all ${
                activeFilter === tab
                  ? "bg-[#00a884] text-white font-semibold"
                  : "bg-[#f0f2f5] dark:bg-[#202c33] text-[#54656f] dark:text-[#8696a0] hover:bg-[#e9edef] dark:hover:bg-[#2a3942]"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Conversation List Stream */}
        <div className="flex-1 overflow-y-auto divide-y divide-[#f0f2f5] dark:divide-[#202c33]">
          {loadingConversations ? (
            <div className="p-8 text-center text-xs text-[#667781] dark:text-[#8696a0] flex flex-col items-center gap-2">
              <ArrowPathIcon className="w-5 h-5 animate-spin text-[#00a884]" />
              <span>Loading webhook chats...</span>
            </div>
          ) : conversations.length === 0 ? (
            <div className="p-8 text-center text-xs text-[#667781] dark:text-[#8696a0] flex flex-col items-center gap-2">
              <span className="p-3 bg-[#f0f2f5] dark:bg-[#202c33] rounded-full text-[#00a884]">
                <FaWhatsapp className="w-6 h-6" />
              </span>
              <p className="font-semibold text-sm text-[#111b21] dark:text-[#e9edef]">No chats received yet</p>
              <p className="text-[11px] max-w-xs leading-relaxed">
                Inbound customer messages received via your WhatsApp Webhooks will automatically appear here.
              </p>
            </div>
          ) : (
            conversations.map((conv) => {
              const contactPhone = conv.str_sender_id || conv.str_receiver_id;
              const contactName = conv.str_contact_name || contactPhone;
              const isSelected = selectedContact?._id === conv._id || selectedContact?.str_sender_id === contactPhone;
              const avatarColor = getAvatarColor(contactName);

              return (
                <div
                  key={conv._id || contactPhone}
                  onClick={() => setSelectedContact(conv)}
                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                    isSelected
                      ? "bg-[#f0f2f5] dark:bg-[#2a3942]"
                      : "hover:bg-[#f5f6f6] dark:hover:bg-[#202c33]"
                  }`}
                >
                  {/* Contact Avatar */}
                  <div className={`w-12 h-12 rounded-full ${avatarColor} text-white flex items-center justify-center font-bold text-sm shrink-0 uppercase shadow-xs select-none`}>
                    {contactName.slice(0, 2)}
                  </div>

                  {/* Thread Summary */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1 mb-1">
                      <h4 className="font-semibold text-xs text-[#111b21] dark:text-[#e9edef] truncate">
                        {contactName}
                      </h4>
                      <span className={`text-[10px] shrink-0 ${conv.int_unread_count > 0 ? "text-[#00a884] font-bold" : "text-[#667781] dark:text-[#8696a0]"}`}>
                        {formatConversationTime(conv.dttm_last_message_at)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between gap-1">
                      <p className="text-[12px] text-[#667781] dark:text-[#8696a0] truncate whitespace-nowrap">
                        {conv.str_last_message_preview || "No messages yet"}
                      </p>

                      <div className="flex items-center gap-1.5 shrink-0">
                        {conv.bool_is_pinned && (
                          <span className="text-[#00a884]" title="Pinned Chat">
                            <BookmarkIcon className="w-3.5 h-3.5" />
                          </span>
                        )}

                        {conv.int_unread_count > 0 && (
                          <span className="bg-[#00a884] text-white font-bold text-[10px] px-1.5 py-0.5 rounded-full min-w-[1.2rem] text-center shadow-xs">
                            {conv.int_unread_count}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ==================================================================== */}
      {/* RIGHT COLUMN: ACTIVE CHAT WINDOW (Responsive: Full screen on mobile when open) */}
      {/* ==================================================================== */}
      <div
        className={`${
          selectedContact ? "flex" : "hidden md:flex"
        } flex-1 flex-col h-full bg-[#efeae2] dark:bg-[#0b141a] relative overflow-hidden`}
      >
        {selectedContact ? (
          <>
            {/* WhatsApp Top Chat Header */}
            <div className="h-16 px-4 bg-[#f0f2f5] dark:bg-[#202c33] flex items-center justify-between border-b border-[#e9edef] dark:border-[#222d34] shrink-0 z-10 shadow-xs">
              <div className="flex items-center gap-3 min-w-0">
                {/* Mobile Back Button */}
                <button
                  onClick={() => setSelectedContact(null)}
                  className="md:hidden p-1.5 -ml-1 text-[#54656f] dark:text-[#aebac1] hover:bg-black/5 dark:hover:bg-white/5 rounded-full"
                  title="Back to Chats"
                >
                  <ChevronLeftIcon className="w-5 h-5" />
                </button>

                <div className={`w-10 h-10 rounded-full ${getAvatarColor(selectedContact.str_contact_name || selectedContact.str_sender_id)} text-white flex items-center justify-center font-bold text-sm shrink-0 uppercase shadow-xs select-none`}>
                  {(selectedContact.str_contact_name || selectedContact.str_sender_id || "U").slice(0, 2)}
                </div>

                <div className="min-w-0">
                  <h3 className="text-sm font-bold text-[#111b21] dark:text-[#e9edef] truncate flex items-center gap-1.5">
                    {selectedContact.str_contact_name || selectedContact.str_sender_id}
                    <span className="w-2 h-2 rounded-full bg-[#00a884] inline-block" title="Online via WhatsApp" />
                  </h3>
                  <p className="text-[11px] text-[#667781] dark:text-[#8696a0] truncate">
                    +{selectedContact.str_sender_id || selectedContact.str_receiver_id}
                    {selectedContact.str_business_number && ` • Business: +${selectedContact.str_business_number}`}
                  </p>
                </div>
              </div>

              {/* Actions Header Icons */}
              <div className="flex items-center gap-1.5 text-[#54656f] dark:text-[#aebac1]">
                <button
                  onClick={(e) => handleTogglePin(e, selectedContact)}
                  className="p-2 hover:bg-black/5 dark:hover:bg-white/5 rounded-full transition-colors"
                  title={selectedContact.bool_is_pinned ? "Unpin Chat" : "Pin Chat"}
                >
                  <BookmarkIcon className={`w-4 h-4 ${selectedContact.bool_is_pinned ? "text-[#00a884]" : ""}`} />
                </button>

                <button
                  onClick={() => loadMessages(selectedContact.str_sender_id || selectedContact.str_receiver_id)}
                  className="p-2 hover:bg-black/5 dark:hover:bg-white/5 rounded-full transition-colors"
                  title="Refresh Messages"
                >
                  <ArrowPathIcon className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Message Stream: WhatsApp Classic Wallpaper with Left & Right Avatars */}
            <div
              className="flex-1 overflow-y-auto px-3 sm:px-8 py-4 space-y-2.5 bg-[#efeae2] dark:bg-[#0b141a]"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 50% 50%, rgba(0,0,0,0.015) 1px, transparent 1px)",
                backgroundSize: "24px 24px",
              }}
            >
              {loadingMessages ? (
                <div className="h-full flex items-center justify-center text-xs text-[#667781] dark:text-[#8696a0] gap-2">
                  <ArrowPathIcon className="w-5 h-5 animate-spin text-[#00a884]" />
                  <span>Loading message history...</span>
                </div>
              ) : messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-xs text-[#667781] dark:text-[#8696a0] gap-2">
                  <div className="bg-white/80 dark:bg-[#202c33]/80 backdrop-blur-xs p-4 rounded-xl shadow-xs text-center max-w-sm">
                    <p className="font-semibold text-sm text-[#111b21] dark:text-[#e9edef] mb-1">
                      Start of conversation
                    </p>
                    <p className="text-[11px] leading-relaxed">
                      Messages received from this customer via official WhatsApp Cloud API, Karix & Pinnacle webhooks.
                    </p>
                  </div>
                </div>
              ) : (
                messages.map((msg, index) => {
                  const isOutbound = !msg.bool_is_from_webhook;
                  const prevMsg = index > 0 ? messages[index - 1] : null;
                  const showDateSeparator =
                    index === 0 ||
                    formatDateSeparator(msg.dttm_created_on) !==
                      formatDateSeparator(prevMsg?.dttm_created_on);

                  const contactName = selectedContact.str_contact_name || selectedContact.str_sender_id || "Customer";
                  const contactColor = getAvatarColor(contactName);

                  return (
                    <React.Fragment key={msg._id || index}>
                      {/* WhatsApp Centered Date Pill */}
                      {showDateSeparator && (
                        <div className="flex justify-center my-3 select-none">
                          <span className="bg-white/90 dark:bg-[#182229]/90 text-[#54656f] dark:text-[#8696a0] text-[11px] font-semibold px-3 py-1 rounded-lg shadow-2xs uppercase tracking-wider">
                            {formatDateSeparator(msg.dttm_created_on)}
                          </span>
                        </div>
                      )}

                      {/* Message Row with Left or Right User Avatar */}
                      <div
                        className={`flex items-end gap-2 my-1.5 ${
                          isOutbound ? "justify-end" : "justify-start"
                        }`}
                      >
                        {/* ── LEFT SIDE: Customer Avatar for Inbound ── */}
                        {!isOutbound && (
                          <div
                            className={`w-7 h-7 rounded-full ${contactColor} text-white flex items-center justify-center font-bold text-[10px] shrink-0 uppercase shadow-xs mb-0.5 select-none`}
                            title={contactName}
                          >
                            {contactName.slice(0, 2)}
                          </div>
                        )}

                        {/* ── MESSAGE BUBBLE ── */}
                        <div
                          className={`max-w-[85%] sm:max-w-[70%] rounded-2xl px-3.5 py-2 shadow-xs relative text-[13.5px] leading-relaxed border border-black/5 dark:border-white/5 ${
                            isOutbound
                              ? "bg-[#d9fdd3] dark:bg-[#005c4b] text-[#111b21] dark:text-[#e9edef] rounded-tr-xs"
                              : "bg-white dark:bg-[#202c33] text-[#111b21] dark:text-[#e9edef] rounded-tl-xs"
                          }`}
                        >
                          {/* Media Display */}
                          {msg.str_media_url && (
                            <div className="mb-1.5 rounded-lg overflow-hidden border border-black/5 dark:border-white/5">
                              {msg.str_message_type === "image" ? (
                                <img
                                  src={resolveMediaUrl(msg.str_media_url)}
                                  alt="WhatsApp Media"
                                  className="max-h-72 w-full object-cover rounded cursor-pointer hover:opacity-95"
                                  onClick={() => window.open(resolveMediaUrl(msg.str_media_url), "_blank")}
                                />
                              ) : msg.str_message_type === "video" ? (
                                <video
                                  controls
                                  src={resolveMediaUrl(msg.str_media_url)}
                                  className="max-h-72 w-full rounded"
                                />
                              ) : msg.str_message_type === "audio" ? (
                                <audio controls src={resolveMediaUrl(msg.str_media_url)} className="w-full my-1" />
                              ) : (
                                <a
                                  href={resolveMediaUrl(msg.str_media_url)}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="flex items-center gap-2.5 p-2.5 bg-black/5 dark:bg-white/5 rounded-lg text-xs font-medium hover:bg-black/10 dark:hover:bg-white/10 transition-colors break-all"
                                >
                                  <DocumentIcon className="w-5 h-5 text-[#00a884] shrink-0" />
                                  <div className="min-w-0">
                                    <p className="font-semibold truncate">Document ({msg.str_message_type || "file"})</p>
                                    <span className="text-[10px] text-[#027eb5] dark:text-[#53bdeb] underline">
                                      Click to download
                                    </span>
                                  </div>
                                </a>
                              )}
                            </div>
                          )}

                          {/* Preserves Exact Copy-Paste and Newlines Formatting */}
                          {msg.str_message && (
                            <div className="whitespace-pre-wrap break-words font-sans">
                              {renderFormattedText(msg.str_message)}
                            </div>
                          )}

                          {/* Timestamp & Status: Inbound shows time; Outbound shows time + status */}
                          <div className="mt-1 flex items-center justify-end">
                            {isOutbound ? (
                              <MessageStatus
                                status={msg.str_status}
                                time={formatMessageTime(msg.dttm_created_on)}
                              />
                            ) : (
                              <span className="text-[11px] text-[#667781] dark:text-[#8696a0] font-sans">
                                {formatMessageTime(msg.dttm_created_on)}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* ── RIGHT SIDE: Agent / User Avatar for Outbound ── */}
                        {isOutbound && (
                          <div
                            className="w-7 h-7 rounded-full bg-[#00a884] text-white flex items-center justify-center font-bold text-[10px] shrink-0 uppercase shadow-xs mb-0.5 select-none"
                            title={currentUserName}
                          >
                            {currentUserInitial}
                          </div>
                        )}
                      </div>
                    </React.Fragment>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* ================================================================ */}
            {/* STAGED MULTI-MEDIA ATTACHMENT PREVIEW TRAY (Before Sending) */}
            {/* ================================================================ */}
            {selectedFiles.length > 0 && (
              <div className="px-4 py-2.5 bg-[#f0f2f5] dark:bg-[#202c33] border-t border-[#e9edef] dark:border-[#222d34] flex flex-col gap-2 shrink-0">
                <div className="flex items-center justify-between text-xs text-[#54656f] dark:text-[#8696a0]">
                  <span className="font-semibold text-[#111b21] dark:text-[#e9edef]">
                    {selectedFiles.length} file{selectedFiles.length === 1 ? "" : "s"} ready to send
                  </span>
                  <button
                    type="button"
                    onClick={clearAllFiles}
                    className="text-xs text-rose-500 hover:underline"
                  >
                    Clear all
                  </button>
                </div>

                <div className="flex items-center gap-2.5 overflow-x-auto pb-1 scrollbar-thin">
                  {selectedFiles.map((item) => (
                    <div
                      key={item.id}
                      className="relative shrink-0 w-24 h-24 rounded-xl bg-white dark:bg-[#2a3942] border border-[#e9edef] dark:border-[#374248] p-1 flex flex-col items-center justify-center text-center shadow-xs"
                    >
                      {/* Remove Button */}
                      <button
                        type="button"
                        onClick={() => removeFile(item.id)}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-rose-500 hover:bg-rose-600 text-white flex items-center justify-center shadow-md transition-transform active:scale-90 z-10"
                        title="Remove file"
                      >
                        <XMarkIcon className="w-3.5 h-3.5" />
                      </button>

                      {item.type === "image" && item.previewUrl ? (
                        <img
                          src={item.previewUrl}
                          alt={item.name}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : item.type === "video" ? (
                        <div className="flex flex-col items-center justify-center text-purple-500 p-1">
                          <FaVideo className="w-6 h-6 mb-1" />
                          <p className="text-[10px] font-medium text-[#111b21] dark:text-[#e9edef] truncate max-w-[5rem]">
                            {item.name}
                          </p>
                        </div>
                      ) : item.type === "audio" ? (
                        <div className="flex flex-col items-center justify-center text-amber-500 p-1">
                          <FaHeadphones className="w-6 h-6 mb-1" />
                          <p className="text-[10px] font-medium text-[#111b21] dark:text-[#e9edef] truncate max-w-[5rem]">
                            {item.name}
                          </p>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center text-blue-500 p-1">
                          <FaFileAlt className="w-6 h-6 mb-1" />
                          <p className="text-[10px] font-medium text-[#111b21] dark:text-[#e9edef] truncate max-w-[5rem]">
                            {item.name}
                          </p>
                        </div>
                      )}

                      <span className="text-[9px] text-[#667781] dark:text-[#8696a0]">
                        {(item.size / 1024).toFixed(0)} KB
                      </span>
                    </div>
                  ))}

                  {/* Add More Files Button */}
                  <button
                    type="button"
                    onClick={() => setShowAttachMenu((prev) => !prev)}
                    className="shrink-0 w-24 h-24 rounded-xl border-2 border-dashed border-[#c1c9cf] dark:border-[#374248] hover:border-[#00a884] flex flex-col items-center justify-center gap-1 text-[#667781] dark:text-[#8696a0] hover:text-[#00a884] transition-colors"
                    title="Add more files"
                  >
                    <span className="text-xl font-bold leading-none">+</span>
                    <span className="text-[10px] font-medium">Add more</span>
                  </button>
                </div>
              </div>
            )}

            {/* Floating Emoji Picker Popover */}
            {showEmojiPicker && (
              <div
                ref={emojiPickerContainerRef}
                className="absolute bottom-16 left-2 sm:left-4 z-50 shadow-2xl rounded-2xl overflow-hidden border border-[#e9edef] dark:border-[#222d34] max-w-[calc(100vw-1rem)]"
              >
                <EmojiPicker
                  onEmojiClick={handleEmojiClick}
                  theme={isDarkMode ? Theme.DARK : Theme.LIGHT}
                  searchPlaceHolder="Search emoji..."
                  width={typeof window !== "undefined" && window.innerWidth < 400 ? window.innerWidth - 32 : 350}
                  height={400}
                />
              </div>
            )}

            {/* ================================================================ */}
            {/* WHATSAPP ATTACHMENT MENU POPOVER (Photos/Videos, Document, Audio) */}
            {/* ================================================================ */}
            {showAttachMenu && (
              <div
                ref={attachMenuRef}
                className="absolute bottom-16 left-12 z-50 bg-white dark:bg-[#202c33] rounded-2xl shadow-2xl border border-[#e9edef] dark:border-[#2a3942] p-2 flex flex-col gap-1 w-52 animate-fade-in"
              >
                {/* 1. Photos & Videos */}
                <button
                  type="button"
                  onClick={() => {
                    imageVideoInputRef.current?.click();
                    setShowAttachMenu(false);
                  }}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#f0f2f5] dark:hover:bg-[#2a3942] transition-colors text-left"
                >
                  <div className="w-9 h-9 rounded-full bg-purple-500/10 text-purple-600 flex items-center justify-center shrink-0">
                    <FaImage className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-[#111b21] dark:text-[#e9edef]">
                      Photos & Videos
                    </p>
                    <p className="text-[10px] text-[#667781] dark:text-[#8696a0]">
                      Images, MP4, clips
                    </p>
                  </div>
                </button>

                {/* 2. Documents */}
                <button
                  type="button"
                  onClick={() => {
                    docInputRef.current?.click();
                    setShowAttachMenu(false);
                  }}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#f0f2f5] dark:hover:bg-[#2a3942] transition-colors text-left"
                >
                  <div className="w-9 h-9 rounded-full bg-blue-500/10 text-blue-600 flex items-center justify-center shrink-0">
                    <FaFileAlt className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-[#111b21] dark:text-[#e9edef]">
                      Document
                    </p>
                    <p className="text-[10px] text-[#667781] dark:text-[#8696a0]">
                      PDF, DOCX, XLS, TXT
                    </p>
                  </div>
                </button>

                {/* 3. Audio */}
                <button
                  type="button"
                  onClick={() => {
                    audioInputRef.current?.click();
                    setShowAttachMenu(false);
                  }}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#f0f2f5] dark:hover:bg-[#2a3942] transition-colors text-left"
                >
                  <div className="w-9 h-9 rounded-full bg-amber-500/10 text-amber-600 flex items-center justify-center shrink-0">
                    <FaHeadphones className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-[#111b21] dark:text-[#e9edef]">
                      Audio
                    </p>
                    <p className="text-[10px] text-[#667781] dark:text-[#8696a0]">
                      MP3, WAV, voice files
                    </p>
                  </div>
                </button>
              </div>
            )}

            {/* Hidden File Inputs for Categories */}
            <input
              type="file"
              ref={imageVideoInputRef}
              onChange={handleFilesSelect}
              accept="image/*,video/*"
              multiple
              className="hidden"
            />
            <input
              type="file"
              ref={docInputRef}
              onChange={handleFilesSelect}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip"
              multiple
              className="hidden"
            />
            <input
              type="file"
              ref={audioInputRef}
              onChange={handleFilesSelect}
              accept="audio/*"
              multiple
              className="hidden"
            />

            {/* ================================================================ */}
            {/* WHATSAPP EXACT BOTTOM CHATBOX INPUT AREA */}
            {/* ================================================================ */}
            <div className="px-3 py-2.5 bg-[#f0f2f5] dark:bg-[#202c33] flex items-end gap-2 border-t border-[#e9edef] dark:border-[#222d34] shrink-0 z-10">
              {/* Emoji Picker Button */}
              <button
                type="button"
                onClick={() => {
                  setShowEmojiPicker((prev) => !prev);
                  setShowAttachMenu(false);
                }}
                className={`p-2 rounded-full transition-colors ${
                  showEmojiPicker
                    ? "text-[#00a884] bg-black/5 dark:bg-white/5"
                    : "text-[#54656f] dark:text-[#8696a0] hover:bg-black/5 dark:hover:bg-white/5"
                }`}
                title="Select Emoji"
              >
                <FaceSmileIcon className="w-6 h-6" />
              </button>

              {/* Attach File Button (Opens Category Popover) */}
              <button
                type="button"
                onClick={() => {
                  setShowAttachMenu((prev) => !prev);
                  setShowEmojiPicker(false);
                }}
                className={`p-2 rounded-full transition-colors ${
                  showAttachMenu
                    ? "text-[#00a884] bg-black/5 dark:bg-white/5"
                    : "text-[#54656f] dark:text-[#8696a0] hover:bg-black/5 dark:hover:bg-white/5"
                }`}
                title="Attach Document or Media"
              >
                <PaperClipIcon className="w-6 h-6" />
              </button>

              {/* Message Textarea Container */}
              <div className="flex-1 bg-white dark:bg-[#2a3942] rounded-lg px-3.5 py-2 flex items-center min-h-[42px] max-h-[120px] shadow-2xs">
                <textarea
                  ref={textareaRef}
                  rows={1}
                  placeholder={
                    selectedFiles.length > 0
                      ? "Add a caption... (Shift + Enter for new line)"
                      : "Type a message (Shift + Enter for new line)"
                  }
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={sending || uploadingMedia}
                  className="w-full bg-transparent text-sm text-[#111b21] dark:text-[#e9edef] placeholder-[#667781] dark:placeholder-[#8696a0] focus:outline-none resize-none leading-relaxed font-sans"
                  style={{ maxHeight: "120px" }}
                />
              </div>

              {/* Send Button */}
              <button
                type="button"
                onClick={handleSendMessage}
                disabled={(!messageInput.trim() && selectedFiles.length === 0) || sending || uploadingMedia}
                className="w-10 h-10 rounded-full bg-[#00a884] hover:bg-[#008f6f] disabled:opacity-40 text-white flex items-center justify-center shadow-md transition-transform active:scale-95 shrink-0"
                title="Send Message (Enter)"
              >
                {sending || uploadingMedia ? (
                  <ArrowPathIcon className="w-5 h-5 animate-spin" />
                ) : (
                  <PaperAirplaneIcon className="w-5 h-5" />
                )}
              </button>
            </div>
          </>
        ) : (
          /* Empty State View */
          <div className="h-full flex flex-col items-center justify-center p-8 text-center bg-[#f0f2f5] dark:bg-[#222e35]">
            <div className="max-w-md flex flex-col items-center">
              <div className="w-24 h-24 rounded-full bg-[#00a884]/10 text-[#00a884] flex items-center justify-center mb-6 shadow-inner select-none">
                <FaWhatsapp className="w-14 h-14 text-[#00a884]" />
              </div>

              <h2 className="text-2xl font-bold text-[#111b21] dark:text-[#e9edef] mb-2.5">
                WhatsApp Live Customer Chat
              </h2>

              <div className="flex items-center gap-1.5 text-[11px] text-[#667781] dark:text-[#8696a0] select-none">
                <FaWhatsapp className="w-3.5 h-3.5 text-[#00a884]" />
                <span>End-to-end encrypted messaging pipeline</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}